import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseInfiniteQueryOptions, useInfiniteQuery } from 'react-query'
import { Response } from 'request-dot-js/index'
import qs from 'qs'
import { CampaignOption } from 'types'
import { getNextPageParam } from 'utils/getNextPageParam'
import { reportError } from 'utils/error'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

type Data = { campaign_options: CampaignOption[] }

interface Params {
  page: number
  items: number
  start_date?: string
  end_date?: string
  order_direction?: 'asc' | 'desc'
  order_by?: 'start_date'
}

export interface Props
  extends Partial<Pick<Params, 'items' | 'start_date' | 'end_date' | 'order_direction' | 'order_by'>> {
  brandPlanId: number
}

export const fetchCampaignOptions = async ({
  workspaceId,
  brandPlanId,
  params,
}: {
  workspaceId: number
  brandPlanId: number
  params: Params
}) => {
  const res = await sendToApi<Data>(
    `/w/${workspaceId}/brand_plans/${brandPlanId}/campaign_options?${qs.stringify(params)}`,
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res
}

export interface Options<TData> extends UseInfiniteQueryOptions<Response<Data>, unknown, TData> {}

export const useCampaignOptions = <TData = Response<Data>,>(
  { brandPlanId, items = 20, start_date, end_date, order_direction = 'asc', order_by = 'start_date' }: Props,
  options?: Options<TData>,
) => {
  const workspaceId = useCurrentWorkspaceId()

  return useInfiniteQuery<Response<Data>, unknown, TData>(
    [
      'workspaces',
      workspaceId,
      'brandPlans',
      brandPlanId,
      'campaignOptions',
      { items, start_date, end_date, order_direction, order_by },
    ],
    ({ pageParam = 1 }) =>
      fetchCampaignOptions({
        workspaceId,
        brandPlanId,
        params: { page: pageParam, items, start_date, end_date, order_direction, order_by },
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      getNextPageParam,
      onError: (err) => reportError(err, { severity: 'error' }),
      ...options,
    },
  )
}
